/**
 * Cross-Tab Refresh Token Lock
 *
 * Problem: When multiple browser tabs share the same localStorage, they also share
 * the same refresh token. If the access token expires, all tabs try to refresh
 * simultaneously. The auth server rotates the refresh token on use, so only the
 * first request succeeds — the rest get 400 BAD REQUEST and the user gets logged out.
 *
 * Solution: Use a localStorage-based lock so only one tab performs the refresh.
 * Other tabs wait for that tab to finish, then read the new tokens from localStorage.
 *
 * How it works:
 * 1. A tab wanting to refresh writes its ID + timestamp to localStorage ("lock")
 * 2. After a short delay, it re-reads the value to confirm it still holds the lock
 *    (if two tabs write simultaneously, the last writer wins — the other detects it lost)
 * 3. Other tabs that see an active lock wait (poll) until the lock is released
 *    or new tokens appear in localStorage
 * 4. If a tab crashes while holding the lock, other tabs treat it as stale after 30s
 */
import { setLocalStorageString, getLocalStorageString, removeLocalStorage, getLocalStorage } from 'utils/fnStorage';

// Unique ID for this tab — used to identify which tab holds the lock
const TAB_ID = Math.random().toString(36).substring(2);

const LOCK_KEY = 'refreshLockHolder' as const;

// If a lock is older than this, assume the holding tab crashed and ignore it
const STALE_LOCK_MS = 30000;

// How often waiting tabs check if the refresh is done
const POLL_INTERVAL_MS = 100;

// Max time a waiting tab will poll before giving up
const POLL_TIMEOUT_MS = 15000;

const parseLockValue = (raw: string | null): { tabId: string; timestamp: number } | null => {
    if (!raw) return null;
    const separatorIndex = raw.indexOf(':');
    if (separatorIndex === -1) return null;
    const tabId = raw.substring(0, separatorIndex);
    const timestamp = parseInt(raw.substring(separatorIndex + 1), 10);
    if (Number.isNaN(timestamp)) return null;
    return { tabId, timestamp };
};

const isLockHeldByAnotherTab = (lock: { tabId: string; timestamp: number } | null): boolean =>
    !!lock && lock.tabId !== TAB_ID && Date.now() - lock.timestamp < STALE_LOCK_MS;

/**
 * Try to become the tab that performs the token refresh.
 * Returns true if this tab now holds the lock, false if another tab already does.
 */
export const acquireRefreshLock = async (): Promise<boolean> => {
    const existingLock = parseLockValue(getLocalStorageString(LOCK_KEY));

    if (isLockHeldByAnotherTab(existingLock)) {
        return false;
    }

    if (existingLock?.tabId === TAB_ID) {
        return true;
    }

    // No active lock (or stale) — claim it
    setLocalStorageString(LOCK_KEY, `${TAB_ID}:${Date.now()}`);

    // Wait briefly, then verify we still hold the lock.
    // If two tabs wrote simultaneously, only the last write persists in localStorage.
    await new Promise((resolve) => {
        setTimeout(resolve, 50);
    });

    const currentLock = parseLockValue(getLocalStorageString(LOCK_KEY));
    return currentLock?.tabId === TAB_ID;
};

/**
 * Release the lock after a successful or failed refresh attempt.
 * Only clears if this tab is the current holder (avoids clearing another tab's lock).
 */
export const releaseRefreshLock = (): void => {
    const currentLock = parseLockValue(getLocalStorageString(LOCK_KEY));
    if (currentLock?.tabId === TAB_ID) {
        removeLocalStorage(LOCK_KEY);
    }
};

/**
 * Wait for another tab to finish its refresh.
 * Resolves true when the lock is released or new tokens appear in localStorage.
 * Resolves false on timeout (the holding tab likely crashed — caller can retry).
 */
export const waitForRefreshCompletion = (currentAccessToken: string | null): Promise<boolean> =>
    new Promise((resolve) => {
        const startTime = Date.now();

        const poll = () => {
            const lock = parseLockValue(getLocalStorageString(LOCK_KEY));
            const storedAccessToken = getLocalStorage('accessToken');

            const lockReleased = !lock;
            const tokensUpdated = !!storedAccessToken && storedAccessToken !== currentAccessToken;

            if (lockReleased || tokensUpdated) {
                resolve(true);
                return;
            }

            if (Date.now() - startTime >= POLL_TIMEOUT_MS) {
                resolve(false);
                return;
            }

            setTimeout(poll, POLL_INTERVAL_MS);
        };

        poll();
    });
